import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from '../components/Layout';
import SubscriptionPaymentCard from "../components/billing/SubscriptionPaymentCard";
import InvoicesList from "../components/billing/InvoicesList";
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { updateAutoRenew } from '../services/billingPhase2Service';
import { toast } from 'sonner';
import "./BillingPage.css";
import { CreditCard, FileText, Question, ClipboardText, ArrowsClockwise, WarningCircle, Envelope, Phone, ChatCircle, Warning, CheckCircle } from '@phosphor-icons/react';

// The 'starter' value keeps its stored value from before a 4th tier
// existed — only its display label changed to "Basic" when Standard was
// inserted below Growth. See services/plan_gating.py's PlanTier docstring.
// Read-only here — plan tier is now a super-admin-only change (see
// components/billing/PlanTierManagement.js), not self-service.
const PLAN_TIER_LABELS = { starter: 'Basic', standard: 'Standard', growth: 'Growth', enterprise: 'Enterprise' };

/**
 * Billing Page
 * Main page for platform subscription billing management
 * Implements fee payment workflow similar to parent portal
 */
const BillingPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("subscription");
  const [metrics, setMetrics] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [config, setConfig] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const invoicesListRef = useRef(null);

  // Load billing data
  const loadBillingData = useCallback(async () => {
    setLoadingBilling(true);
    try {
      const [metricsResult, subscriptionResult, configResult] = await Promise.allSettled([
        api.get('/api/billing/metrics'),
        api.get('/api/billing/subscriptions/current'),
        user?.school_id ? api.get(`/api/billing/config/${user.school_id}`) : Promise.resolve(null)
      ]);

      if (metricsResult.status === 'rejected') {
        throw metricsResult.reason;
      }
      setMetrics(metricsResult.value);

      // A school may not have generated its first platform subscription yet.
      // That endpoint intentionally returns 404 for this empty state; it
      // should not prevent the rest of the billing page from loading.
      if (subscriptionResult.status === 'fulfilled') {
        setSubscription(subscriptionResult.value);
      } else if (subscriptionResult.reason?.status === 404) {
        setSubscription(null);
      } else {
        throw subscriptionResult.reason;
      }

      if (configResult.status === 'fulfilled') {
        setConfig(configResult.value);
      } else {
        throw configResult.reason;
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoadingBilling(false);
    }
  }, [user?.school_id]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  // Handle payment success - reload billing data
  const handlePaymentSuccess = useCallback(() => {
    toast.success('Payment completed successfully!');
    setTimeout(() => {
      loadBillingData();
      // Refresh invoices list to show updated status
      if (invoicesListRef.current?.refresh) {
        invoicesListRef.current.refresh();
      }
    }, 2000);
  }, [loadBillingData]);

  const pageContent = (
    <div className="page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Platform Billing</h1>
          <p>Manage your school's subscription </p>
        </div>
      </div>

      {/* Tabs Container */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === "subscription" ? "active" : ""}`}
          onClick={() => setActiveTab("subscription")}
          title="Subscription Status"
        >
          <CreditCard size={18} weight="fill" />
          <span className="ms-2">Subscription Status</span>
        </button>
        <button
          className={`tab-btn ${activeTab === "invoices" ? "active" : ""}`}
          onClick={() => setActiveTab("invoices")}
          title="Invoices"
        >
          <FileText size={18} weight="fill" />
          <span className="ms-2">Invoices</span>
        </button>
        <button
          className={`tab-btn ${activeTab === "help" ? "active" : ""}`}
          onClick={() => setActiveTab("help")}
          title="Help & FAQ"
        >
          <Question size={18} weight="fill" />
          <span className="ms-2">Help & FAQ</span>
        </button>
      </div>

      {/* Page Content */}
      <div className="page-content">
        {activeTab === "subscription" && (
          <SubscriptionStatusTab
            metrics={metrics}
            subscription={subscription}
            config={config}
            schoolId={user?.school_id}
            loadingBilling={loadingBilling}
            onPaymentSuccess={handlePaymentSuccess}
            onConfigChange={setConfig}
          />
        )}
        {activeTab === "invoices" && <InvoicesTab ref={invoicesListRef} subscription={subscription} />}
        {activeTab === "help" && <HelpTab />}
      </div>
    </div>
  );

  return <Layout>{pageContent}</Layout>;
};

// Subscription Status Tab Component
const SubscriptionStatusTab = ({ metrics, subscription, config, schoolId, loadingBilling, onPaymentSuccess, onConfigChange }) => {
  const [togglingRenew, setTogglingRenew] = useState(false);
  const [renewError, setRenewError] = useState(null);

  if (loadingBilling) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading billing information...</p>
      </div>
    );
  }

  const daysOverdue = metrics && metrics.is_overdue ? Math.abs(metrics.days_until_due) : 0;
  const daysUntilSuspension = config
    ? Math.max(config.grace_period_days - daysOverdue, 0)
    : null;

  const handleAutoRenewToggle = async () => {
    if (!config || !schoolId) return;
    const next = !config.auto_renew_enabled;
    setTogglingRenew(true);
    setRenewError(null);
    // Optimistic update — rolled back below if the request fails.
    onConfigChange({ ...config, auto_renew_enabled: next });
    try {
      const updated = await updateAutoRenew(schoolId, next);
      onConfigChange(updated);
    } catch (error) {
      onConfigChange(config);
      setRenewError(error?.error || 'Failed to update auto-renew setting');
    } finally {
      setTogglingRenew(false);
    }
  };

  return (
    <div className="billing-section">
      {metrics && metrics.is_overdue && config && (
        <div className={`alert-banner ${daysUntilSuspension <= 2 ? 'alert-danger' : 'alert-warning'}`}>
          <Warning size={20} weight="fill" />
          <div>
            <h3>Payment {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue</h3>
            <p>
              {daysUntilSuspension > 0
                ? `Access will be suspended in ${daysUntilSuspension} ${daysUntilSuspension === 1 ? 'day' : 'days'} if this isn't resolved.`
                : "Access may be suspended at any time. Pay now to avoid interruption."}
            </p>
          </div>
        </div>
      )}

      <div className="billing-info-card">
        <h3><ClipboardText size={20} className="me-2" /> Billing Information</h3>
        <p className="billing-info">
          <strong>Platform Subscription Fee:</strong> GHS 400 per student per academic term.
          This fee allows your school unlimited access to the School ERP platform including all
          features and updates.
        </p>
        {config && (
          <p className="billing-info">
            <strong>Current plan:</strong> {PLAN_TIER_LABELS[config.plan_tier] || config.plan_tier}
          </p>
        )}
      </div>

      {metrics && subscription ? (
        <SubscriptionPaymentCard
          metrics={metrics}
          subscription={subscription}
          onPaymentSuccess={onPaymentSuccess}
        />
      ) : (
        <div className="empty-state-card">
          <p>Unable to load billing information. Please try again.</p>
        </div>
      )}

      {config && (
        <div className="billing-info-card">
          <h3><ArrowsClockwise size={20} className="me-2" /> Billing &amp; Renewal</h3>
          {config.paystack_card_last4 ? (
            <p className="billing-info">
              <CheckCircle size={16} weight="fill" className="me-2" style={{ color: 'var(--primary)' }} />
              Card on file: {config.paystack_card_brand ? `${config.paystack_card_brand} ` : ''}•••• {config.paystack_card_last4}
            </p>
          ) : (
            <p className="billing-info">
              No card on file yet — your first payment on this page will save your card for
              automatic renewal, unless you turn auto-renew off below.
            </p>
          )}
          <label className="form-toggle-row">
            <input
              type="checkbox"
              checked={!!config.auto_renew_enabled}
              onChange={handleAutoRenewToggle}
              disabled={togglingRenew}
            />
            <span>
              Auto-renew {config.auto_renew_enabled ? 'on' : 'off'} — automatically charge the card
              on file when a new invoice is due.
            </span>
          </label>
          {renewError && <p className="billing-info" style={{ color: '#DC2626' }}>{renewError}</p>}
        </div>
      )}
    </div>
  );
};

// Invoices Tab Component
const InvoicesTab = React.forwardRef(({ subscription }, ref) => {
  return (
    <div className="billing-section">
      <InvoicesList ref={ref} subscription={subscription} />
    </div>
  );
});

InvoicesTab.displayName = "InvoicesTab";

// Help & FAQ Tab Component
const HelpTab = () => {
  return (
    <div className="billing-help">
      <h2>Frequently Asked Questions</h2>

      <div className="faq-item">
        <h5><Question size={18} className="me-2" /> What is the platform subscription fee?</h5>
        <p>
          The platform subscription fee is GHS 400 per student per academic term (GHS 1,200 per
          student annually for a 3-term school year). This fee covers unlimited access to all
          School ERP features including student management, payroll, finance, assignments,
          communication, and more.
        </p>
      </div>

      <div className="faq-item">
        <h5><CreditCard size={18} className="me-2" /> How do I make a payment?</h5>
        <p>
          Go to the "Subscription Status" tab and click the "Pay Now" button. You'll be
          redirected to our secure Paystack payment gateway where you can pay using:
        </p>
        <ul>
          <li>Mobile Money (MTN, Vodafone, AirtelTigo)</li>
          <li>Debit/Credit Cards (Visa, Mastercard)</li>
        </ul>
      </div>

      <div className="faq-item">
        <h5><ArrowsClockwise size={18} className="me-2" /> Can I make partial payments?</h5>
        <p>
          Yes, you can make partial payments. During the payment process, select "Partial
          Payment" and enter the amount you want to pay. Your account will show the remaining
          balance and you can settle it later.
        </p>
      </div>

      <div className="faq-item">
        <h5><ClipboardText size={18} className="me-2" /> When is payment due?</h5>
        <p>
          Payment is calculated at the start of each academic term based on the number of
          active students. The due date is typically 30 days from the billing date, but your
          specific due date will be shown in the "Subscription Status" tab.
        </p>
      </div>

      <div className="faq-item">
        <h5><WarningCircle size={18} className="me-2" /> What happens if I don't pay on time?</h5>
        <p>If payment is not made by the due date:</p>
        <ul>
          <li>Your subscription status will be marked as overdue</li>
          <li>You may receive reminder communications</li>
          <li>After a grace period, your school's platform access may be suspended</li>
          <li>Contact support immediately if you need to arrange an alternative payment plan</li>
        </ul>
      </div>

      <div className="faq-item">
        <h5><FileText size={18} className="me-2" /> How do I download an invoice?</h5>
        <p>
          Go to the "Invoices" tab to see all your subscription invoices. Click the download
          icon (<FileText size={14} />) next to any invoice to download a PDF copy for your records.
        </p>
      </div>

      <div className="faq-item">
        <h5><Question size={18} className="me-2" /> Who should I contact for billing support?</h5>
        <p>For billing inquiries or issues:</p>
        <ul>
          <li><Envelope size={16} className="me-2" /> Email: info@campusio.online</li>
          <li><Phone size={16} className="me-2" /> Call: +233 53 448 4781(Business hours)</li>
          <li><ChatCircle size={16} className="me-2" /> Watsapp: +233 53 448 4781 </li>
        </ul>
      </div>

      <div className="help-footer">
        <strong>Need more help?</strong> Visit our{" "}
        <a href="/billing-docs" rel="noopener noreferrer">
          billing documentation
        </a>
        {" "}or contact our support team.
      </div>
    </div>
  );
};

export default BillingPage;
